/**
 * NdaAdmin — /admin/nda. Two jobs:
 *   1. Signature log: who signed, when, from which IP, NDA version.
 *   2. Document editor: paste/update the verbatim NDA text (bumps version).
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import AdminHubNav from "../components/AdminHubNav";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FileSignature, Save, Loader, ShieldCheck, RefreshCw } from "lucide-react";

export default function NdaAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sigs, setSigs] = useState([]);
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get("/admin/nda/signatures"),
        api.get("/nda/document"),
      ]);
      setSigs(s.data.signatures || []);
      setDoc(d.data);
      setTitle(d.data.title);
      setText(d.data.text);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && !user.is_admin) navigate("/");
    else if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const save = async () => {
    setSaving(true);
    setSavedMsg("");
    setError("");
    try {
      const { data } = await api.put("/admin/nda/document", { title: title.trim(), text: text.trim() });
      setSavedMsg(`Saved — now version ${data.version}. New signers see this text; existing signatures stay tied to the version they signed.`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <AdminHubNav active="nda" />
        <div className="rm-tag mb-3">Admin · NDA</div>
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-2">NDA Sign-offs</h1>
        <p className="text-sm mb-8" style={{ color: "#475569" }}>
          New testers hit the NDA wall right after login and sign in-app — no DocuSign round-trip.
          Signature audit trail below; edit the agreement text on the right.
        </p>

        {error && (
          <div className="p-3 mb-6 rounded-md font-mono-rm text-sm"
               style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }}
               data-testid="nda-admin-error">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Signatures */}
          <div className="rm-card-static p-5" data-testid="nda-admin-signatures">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <ShieldCheck size={16} style={{ color: "#0E5A4A" }} />
                <h2 className="font-display text-xl uppercase leading-tight">
                  Signatures ({sigs.length})
                </h2>
              </div>
              <button onClick={load} className="rm-btn rm-btn-ghost text-xs px-2 py-1" data-testid="nda-admin-refresh">
                <RefreshCw size={13} />
              </button>
            </div>
            {loading ? (
              <div className="py-8 text-center font-mono-rm text-[11px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
                <Loader size={14} className="animate-spin inline mr-2" /> Loading
              </div>
            ) : sigs.length === 0 ? (
              <div className="py-8 text-center font-mono-rm text-[11px] uppercase tracking-widest"
                   style={{ color: "#94A3B8" }} data-testid="nda-admin-empty">
                · No signatures yet — new testers will appear here the moment they sign
              </div>
            ) : (
              <div className="space-y-2" style={{ maxHeight: 520, overflowY: "auto" }}>
                {sigs.map((s) => (
                  <div key={s.id} className="p-3 rounded-md"
                       style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                       data-testid={`nda-sig-${s.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-semibold text-[13.5px]" style={{ color: "#0F1B2D" }}>
                        {s.full_name}
                      </div>
                      <span className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
                            style={{ background: "#D1FAE5", color: "#0E5A4A", fontWeight: 700 }}>
                        v{s.nda_version}
                      </span>
                    </div>
                    <div className="font-mono-rm text-[11px] mt-0.5" style={{ color: "#475569" }}>
                      {s.email}
                    </div>
                    <div className="font-mono-rm text-[10px] uppercase tracking-wide mt-1" style={{ color: "#94A3B8" }}>
                      {s.signed_at ? new Date(s.signed_at).toLocaleString() : "—"} · IP {s.ip}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Document editor */}
          <div className="rm-card-static p-5" data-testid="nda-admin-editor">
            <div className="flex items-center gap-2 mb-1">
              <FileSignature size={16} style={{ color: "#FF6A00" }} />
              <h2 className="font-display text-xl uppercase leading-tight">Agreement text</h2>
            </div>
            <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-4" style={{ color: "#94A3B8" }}>
              {doc ? `Current version ${doc.version} · updated ${doc.updated_at ? new Date(doc.updated_at).toLocaleString() : "—"}` : ""}
            </div>
            <label className="block mb-3">
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1" style={{ color: "#475569", fontWeight: 700 }}>
                · Title
              </div>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                     className="w-full rounded px-3 py-2 text-[13px]"
                     style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                     data-testid="nda-admin-title" />
            </label>
            <label className="block mb-3">
              <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1" style={{ color: "#475569", fontWeight: 700 }}>
                · Full agreement text (paste your NDA verbatim)
              </div>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={18}
                        className="w-full rounded px-3 py-2 text-[12px] leading-relaxed"
                        style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC", resize: "vertical", fontFamily: "ui-monospace, monospace" }}
                        data-testid="nda-admin-text" />
            </label>
            {savedMsg && (
              <div className="p-2.5 rounded mb-3 text-[12px]"
                   style={{ background: "#ECFDF5", border: "1.5px solid #A7F3D0", color: "#065F46" }}
                   data-testid="nda-admin-saved">
                {savedMsg}
              </div>
            )}
            <button onClick={save} disabled={saving || text.trim().length < 50}
                    className="rm-btn rm-btn-primary text-sm disabled:opacity-50"
                    data-testid="nda-admin-save">
              {saving ? <Loader size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save agreement (bumps version)"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
